import { array, lazy, mixed, object, string, type AnySchema } from 'yup';
import { isElementDefinition } from './elementDefinition';
import type { ElementDefinition, RenderContextKind, ReportElementConstructor } from './types';

interface BuildStructureYupSchemaBaseOptions {
    contextKind: RenderContextKind;
    elementClasses: readonly ReportElementConstructor[];
}

interface BuildDeepStructureYupSchemaOptions extends BuildStructureYupSchemaBaseOptions {
    target: 'structure';
}

interface BuildElementSettingsYupSchemaOptions extends BuildStructureYupSchemaBaseOptions {
    target: 'element-settings';
    definition: ElementDefinition;
}

type BuildStructureYupSchemaOptions =
    | BuildDeepStructureYupSchemaOptions
    | BuildElementSettingsYupSchemaOptions;

function buildShallowDefinitionsSchema(): AnySchema {
    return array().of(
        mixed().test(
            'element-definition-shape',
            'Invalid element definition.',
            function test(value) {
                if (isElementDefinition(value)) {
                    return true;
                }

                const nestedPath = typeof this.path === 'string' ? this.path : '';
                return this.createError({
                    message: `Invalid element definition at "${nestedPath}".`,
                });
            }
        )
    );
}

function markSchemaRequired(schema: AnySchema, message?: string): AnySchema {
    // Yup's base Schema.required() returns `any` in the installed version's
    // typings. A future Yup upgrade may make this cast unnecessary; until then,
    // keep the adapter local instead of leaking casts into element contracts.
    return schema.required(message) as AnySchema;
}

export function buildStructureYupSchema(options: BuildStructureYupSchemaOptions): AnySchema {
    // Validates template/element settings payload values (structure data
    // contracts), not developer-authored form-field metadata.
    const compatibleElementClasses = options.elementClasses.filter((ElementClass) =>
        ElementClass.compatibleContextKinds.includes(options.contextKind)
    );
    const supportedTypes = compatibleElementClasses.map((ElementClass) => ElementClass.elementType);
    const elementClassByType = new Map<string, ReportElementConstructor>(
        compatibleElementClasses.map((ElementClass) => [ElementClass.elementType, ElementClass])
    );
    let deepDefinitionsSchema: AnySchema | null = null;

    function buildSettingsSchemaForDefinition(
        definition: ElementDefinition,
        buildDefinitionsSchema: () => AnySchema
    ): AnySchema {
        const ElementClass = elementClassByType.get(definition.type);
        if (!ElementClass) {
            return object().required('Expected element definition settings object.');
        }

        return ElementClass.buildStructureSettingsYupSchema({
            buildDefinitionsSchema,
            buildRequiredDefinitionsSchema: () => markSchemaRequired(buildDefinitionsSchema()),
        });
    }

    function buildDeepDefinitionsSchema(): AnySchema {
        if (deepDefinitionsSchema) {
            return deepDefinitionsSchema;
        }

        deepDefinitionsSchema = array()
            .required('Expected structure definition list.')
            .of(
                lazy((value: unknown) => {
                    const definition = isElementDefinition(value)
                        ? value
                        : {
                              type: '',
                              settings: {},
                          };
                    const settingsSchema = markSchemaRequired(
                        buildSettingsSchemaForDefinition(definition, buildDeepDefinitionsSchema)
                    );

                    return object({
                        type: string()
                            .required('Expected element definition type.')
                            .oneOf(
                                supportedTypes,
                                'Unsupported element type in structure definition list.'
                            ),
                        settings: settingsSchema,
                    })
                        .required('Expected element definition object.')
                        .noUnknown();
                })
            );
        return deepDefinitionsSchema;
    }

    function buildElementSettingsSchema(definition: ElementDefinition): AnySchema {
        return buildSettingsSchemaForDefinition(
            definition,
            options.target === 'structure'
                ? buildDeepDefinitionsSchema
                : buildShallowDefinitionsSchema
        );
    }

    if (options.target === 'element-settings') {
        return buildElementSettingsSchema(options.definition);
    }

    return buildDeepDefinitionsSchema();
}
